import { useEffect, useRef, useState } from "react";

/**
 * Hook that ensures a loading state is displayed for a minimum amount of time.
 * This prevents UI flickering when loading operations complete very quickly.
 * The minimum time includes the actual loading duration (e.g., a 50ms operation
 * with a 400ms minimum will show the spinner for 400ms total, not 450ms).
 *
 * @param loading - The current loading state
 * @param minDisplayMs - Minimum time to display loading (default: 400ms)
 * @returns A debounced loading state that stays true for at least minDisplayMs
 */
export const useMinimumLoadingTime = (
  loading: boolean | undefined,
  minDisplayMs = 400,
): boolean => {
  const [showLoading, setShowLoading] = useState(loading ?? false);
  const loadingStartedAt = useRef<number | null>(null);

  useEffect(() => {
    if (loading) {
      // Loading started - record the time and show immediately
      loadingStartedAt.current = Date.now();
      setShowLoading(true);
    } else if (loadingStartedAt.current !== null) {
      // Loading ended - ensure minimum display time has passed
      const elapsed = Date.now() - loadingStartedAt.current;
      const remaining = Math.max(0, minDisplayMs - elapsed);

      const timeoutId = setTimeout(() => {
        setShowLoading(false);
        loadingStartedAt.current = null;
      }, remaining);

      // React calls this cleanup before next effect run, cancelling pending timeouts
      return () => clearTimeout(timeoutId);
    } else {
      setShowLoading(false);
    }
  }, [loading, minDisplayMs]);

  return showLoading;
};
